import React from 'react'
import Emoji from '@tiptap-pro/extension-emoji'
import { ReactRenderer, ReactNodeViewRenderer, mergeAttributes } from '@tiptap/react'
import { PluginKey } from '@tiptap/pm/state'
import Node from './node'
import Database from 'emoji-picker-element/database'
import Suggestions from './suggestions'
import tippy from 'tippy.js'
import { omit } from 'utilities/object'
import { getTippySuggestionsConfiguration } from '../utilities'

const emojiDatabase = new Database()

const CustomEmoji = Emoji.extend({
    addAttributes() {
        return {
            ...Emoji.config.addAttributes(),
            emoji: {
                default: null,
                parseHTML: element => element.dataset.emoji,
                renderHTML: attributes => ({ 'data-emoji': attributes.emoji })
            }
        }
    },

    addNodeView() {
        return ReactNodeViewRenderer(props => (
            <Node
                {...props}
                extension={this} />
        ))
    },

    renderHTML({ HTMLAttributes }) {
        const attributes = mergeAttributes(
            this.options.HTMLAttributes,
            HTMLAttributes,
            { 'data-type': 'emoji' }
        )

        return [
            'span',
            attributes,
            this.options.renderText(attributes.emoji)
        ]
    },

    renderText({ node }) {
        return this.options.renderText(node.attrs.emoji)
    },

    addOptions() {
        return {
            ...omit(Emoji.options, 'HTMLAttributes'),

            renderText: emoji => emoji ?? '',

            suggestion: {
                ...Emoji.options.suggestion,
                pluginKey: new PluginKey('emoji-suggestions'),

                items: async ({ query }) => {
                    if(!query) {
                        return []
                    }

                    const matches = await emojiDatabase.getEmojiBySearchQuery(query)

                    return matches
                        ?.map(({ shortcodes, unicode }) => ({
                            name: shortcodes[0],
                            emoji: unicode
                        }))
                        ?.slice(0, 5) ?? []
                },

                render: () => {
                    const supportsPopover = Object.prototype.hasOwnProperty.call(HTMLElement.prototype, 'popover')
                    let suggestions
                    let popup

                    return {
                        onStart: props => {
                            suggestions = new ReactRenderer(Suggestions, {
                                props,
                                editor: props.editor
                            })

                            if(!props.clientRect) {
                                return
                            }

                            popup = tippy('body', getTippySuggestionsConfiguration(props, suggestions))

                            if(supportsPopover) {
                                popup?.[0]?.popper?.setAttribute('popover', 'auto')
                                popup?.[0]?.popper?.showPopover?.()
                            }
                        },

                        onUpdate: props => {
                            suggestions?.updateProps(props)

                            if(!props.clientRect) {
                                return
                            }

                            popup?.[0]?.setProps({
                                getReferenceClientRect: props.clientRect
                            })
                        },

                        onKeyDown: props => {
                            if(props.event.key === 'Escape') {
                                popup?.[0]?.hide()
                                return true
                            }

                            return suggestions?.ref?.onKeyDown(props)
                        },

                        onExit: () => {
                            suggestions?.destroy()
                            popup?.[0]?.destroy()
                        }
                    }
                }
            }
        }
    }
})

export default CustomEmoji